import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Clock, ArrowRight, Loader2, MailCheck, KeyRound, ShieldAlert, TimerOff } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  // password tab
  const [pwEmail, setPwEmail] = useState("");
  const [password, setPassword] = useState("");
  // code tab
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [challenge, setChallenge] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const [locked, setLocked] = useState(false);
  const idleExpired = new URLSearchParams(window.location.search).get("expired") === "idle";
  const onAuthError = (err) => {
    if (err.response?.status === 423) setLocked(true);
    toast.error(apiError(err.response?.data?.detail));
  };

  const finish = async (data) => {
    if (data.mfa_required) { setChallenge(data.challenge_token); return; }
    await login(data.token, data.user, data.company);
    toast.success("Welcome back!");
    navigate(dest(data.user));
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login/mfa", { challenge_token: challenge, code: mfaCode });
      await login(data.token, data.user, data.company);
      toast.success("Welcome back!");
      navigate(dest(data.user));
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const dest = (u) => u?.role === "platform_admin" ? "/control-center" : (new URLSearchParams(window.location.search).get("next") || "/app");

  const passwordLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email: pwEmail, password });
      await finish(data);
    } catch (err) {
      onAuthError(err);
    } finally { setBusy(false); }
  };

  const requestCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/request-code", { email });
      toast.success("If that email has an account, a sign-in code is on its way.");
      setStep("code");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/verify-code", { email, code });
      await finish(data);
    } catch (err) { onAuthError(err); }
    finally { setBusy(false); }
  };

  const inputCls = "mt-1.5 bg-[#0A0D0C] border-emerald-500/20";

  return (
    <div className="min-h-screen bg-[#0A0D0C] grid-bg flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="w-9 h-9 rounded-lg bg-emerald-500 flex items-center justify-center"><Clock className="w-5 h-5 text-[#062217]" /></div>
          <span className="font-display text-2xl font-extrabold tracking-tight">QwikLogs Africa</span>
        </Link>
        <div className="card-emerald p-8 animate-fade-up">
          <h1 className="font-display text-2xl font-bold">Sign in</h1>
          <p className="text-sm text-muted-foreground mt-1">Welcome back to your workspace.</p>
          {idleExpired && !locked && (
            <div className="mt-4 flex gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200" data-testid="idle-expired-notice">
              <TimerOff className="w-4 h-4 mt-0.5 shrink-0" /> You were signed out after 30 minutes of inactivity.
            </div>
          )}
          {locked && (
            <div className="mt-4 flex gap-2 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-200" data-testid="account-locked-notice">
              <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
              <span>This account is locked after 5 incorrect password attempts. <Link to="/forgot-password" className="underline font-semibold" data-testid="locked-reset-link">Reset your password</Link> to unlock it, or ask your administrator.</span>
            </div>
          )}

          <Tabs defaultValue="password" className="mt-6">
            {challenge && (
              <form onSubmit={submitMfa} className="space-y-4" data-testid="mfa-challenge-form">
                <div className="flex items-center gap-2 text-emerald-300 text-sm"><KeyRound className="w-4 h-4" /> Enter the 6-digit code from your authenticator app</div>
                <Input inputMode="numeric" required value={mfaCode} onChange={(e) => setMfaCode(e.target.value)} placeholder="000000" maxLength={6} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20 font-mono text-center text-2xl tracking-[0.4em]" data-testid="mfa-login-input" />
                <Button type="submit" disabled={busy} className="w-full bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="mfa-login-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify"}</Button>
                <button type="button" onClick={() => setChallenge(null)} className="text-sm text-muted-foreground hover:text-foreground w-full text-center">Back</button>
              </form>
            )}
            <div className={challenge ? "hidden" : ""}>
            <TabsList className="grid grid-cols-2 bg-[#0A0D0C] border border-emerald-500/15">
              <TabsTrigger value="password" data-testid="tab-password"><KeyRound className="w-4 h-4 mr-1.5" /> Password</TabsTrigger>
              <TabsTrigger value="code" data-testid="tab-code"><MailCheck className="w-4 h-4 mr-1.5" /> Email code</TabsTrigger>
            </TabsList>

            <TabsContent value="password" className="mt-5">
              <form onSubmit={passwordLogin} className="space-y-4">
                <div>
                  <Label htmlFor="pwemail">Work email</Label>
                  <Input id="pwemail" type="email" required value={pwEmail} onChange={(e) => setPwEmail(e.target.value)} placeholder="you@company.com" className={inputCls} data-testid="login-password-email-input" />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="pw">Password</Label>
                    <Link to="/forgot-password" className="text-xs text-emerald-400 hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
                  </div>
                  <Input id="pw" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={inputCls} data-testid="login-password-input" />
                </div>
                <Button type="submit" disabled={busy} className="w-full bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="login-password-submit-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Sign in <ArrowRight className="w-4 h-4 ml-2" /></>}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="code" className="mt-5">
              {step === "email" ? (
                <form onSubmit={requestCode} className="space-y-4">
                  <div>
                    <Label htmlFor="email">Work email</Label>
                    <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className={inputCls} data-testid="login-email-input" />
                  </div>
                  <Button type="submit" disabled={busy} className="w-full bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="login-request-code-btn">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send code <ArrowRight className="w-4 h-4 ml-2" /></>}
                  </Button>
                </form>
              ) : (
                <form onSubmit={verify} className="space-y-4">
                  <div className="flex items-center gap-2 text-emerald-300 text-sm"><MailCheck className="w-4 h-4" /> Code sent to {email}</div>
                  <div>
                    <Label htmlFor="code">6-digit code</Label>
                    <Input id="code" inputMode="numeric" required value={code} onChange={(e) => setCode(e.target.value)} placeholder="000000" maxLength={6} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20 font-mono text-center text-2xl tracking-[0.4em]" data-testid="login-code-input" />
                  </div>
                  <Button type="submit" disabled={busy} className="w-full bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="login-verify-btn">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify & sign in"}
                  </Button>
                  <button type="button" onClick={() => setStep("email")} className="text-sm text-muted-foreground hover:text-foreground w-full text-center">Use a different email</button>
                </form>
              )}
            </TabsContent>
            </div>
          </Tabs>
        </div>
        <p className="text-center text-sm text-muted-foreground mt-6">
          New company? <Link to="/signup" className="text-emerald-400 hover:underline">Create an account</Link>
        </p>
      </div>
    </div>
  );
}
